import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Alert, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

import useFetchById from 'hooks/useFetchById';

import SubtleBadge from 'components/common/SubtleBadge';
import VendorForm from 'components/purchase-order/vendor/VendorForm';
import VendorViewMode from 'components/purchase-order/vendor/VendorDetails/VendorViewMode';

import auth from 'api/core/auth';
import { vendorApi, adminVendorApi } from 'api';
import { formatStatus } from 'utils/valueFormatters';
import { getVendorBadgeColor } from 'constants/vendor';

const VendorDetailInfo = () => {
  const { vendorId } = useParams();

  const [isEditing, setIsEditing] = useState(false);

  const [updateLoading, setIsUpdateLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);

  // Get Vendor data
  const {
    data: vendor,
    loading: fetchLoading,
    error: fetchError,
    setData: setVendor,
    refetch: vendorRefetch,
  } = useFetchById(vendorApi, vendorId);

  // Vendor Update
  const handleSubmit = async values => {
    setIsUpdateLoading(true);

    try {
      const response = await vendorApi.updateVendor(vendorId, values);

      setIsEditing(false);
      toast.success('Vendor updated successfully');

      // Vendor 수정 후, vendor 정보 fetch
      await vendorRefetch();
    } catch (err) {
      toast.error('Failed to update vendor');
    } finally {
      setIsUpdateLoading(false);
    }
  };

  // Vendor 승인 요청
  const handleRequestApprove = async () => {
    setRequestLoading(true);

    try {
      const response = await vendorApi.requestApproveVendor(vendorId);

      toast.success('Request approved successfully');

      // Vendor 승인 요청 후, vendor 정보 fetch
      await vendorRefetch();
    } catch (err) {
      toast.error('Failed to request approval');
    } finally {
      setRequestLoading(false);
    }
  };

  // Vendor 요청 승인
  const handleApproveRequest = async () => {
    setApproveLoading(true);

    try {
      const response = await adminVendorApi.approveVendor(vendorId);

      toast.success('Approved successfully');

      // Vendor 승인 후, vendor 정보 fetch
      await vendorRefetch();
    } catch (err) {
      toast.error('Failed to approve');
    } finally {
      setApproveLoading(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  if (fetchLoading) {
    return (
      <Card className="h-100">
        <Card.Body>
          <div className="text-center">Loading...</div>
        </Card.Body>
      </Card>
    );
  }

  if (fetchError) {
    return (
      <Card className="h-100">
        <Card.Body>
          <Alert variant="danger">{fetchError}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <h5 className="mb-0 me-3">
            Vendor List {'>'} {vendor?.name}
          </h5>

          <SubtleBadge pill bg={getVendorBadgeColor(vendor?.vendor_status)}>
            {formatStatus(vendor?.vendor_status)}
          </SubtleBadge>
        </div>

        <div className="d-flex gap-2">
          {auth?.isAdmin() &&
            (vendor?.vendor_request !== null ||
              vendor?.vendor_status === 'waiting_for_approval') && (
              <Button
                variant="primary"
                size="sm"
                disabled={approveLoading}
                onClick={handleApproveRequest}
              >
                Approve Vendor
              </Button>
            )}

          {vendor?.vendor_status === 'draft' && (
            <Button
              variant="primary"
              size="sm"
              disabled={requestLoading}
              onClick={handleRequestApprove}
            >
              Request Approval
            </Button>
          )}
        </div>
      </div>

      <Card>
        <Card.Body>
          {isEditing ? (
            <VendorForm
              vendor={vendor}
              isLoading={updateLoading}
              onSubmit={handleSubmit}
              onCancel={handleCancelEdit}
            />
          ) : (
            <VendorViewMode
              vendor={vendor}
              isEditing={isEditing}
              handleEditClick={handleEditClick}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default VendorDetailInfo;
